import { VdTracking } from '@vend/vend.js'
import debounce from 'lodash/debounce'
import Mousetrap from 'mousetrap'
import type {
  ModalFactory,
  VdToastNotificationService,
} from '../../vend-ui/types'

import { ROUTES, STATES } from '../../../../lib/routes'
import { EVENT } from '../../constants'
import { TrainingModeManager } from '../../managers/training-mode-manager'
import { WebRegisterStatusManager } from '../../managers/web-register-status-manager'
import {
  $log,
  $document,
  $q,
  $rootScope,
  $state,
} from '../../ng-compat/imports'
import { HotkeysModal as HotkeysModalController } from './hotkeys-modal/hotkeys-modal.controller'
import template from './hotkeys-modal/hotkeys-modal.html'

interface VdOverlayService {
  isVisible(): boolean
}

type Combo = string | string[]

export interface HotkeyInfo {
  combo: Combo
  event?: 'keypress' | 'keydown' | 'keyup'
  summary?: string
  count?: boolean
}

export interface Hotkey extends HotkeyInfo {
  action: () => void
}

const COMBO_TO_SHOW_TIPS = 'alt'
const SHOW_HOTKEY_TIPS_CLASS = 'show-hotkey-tips'

export class HotkeysService {
  public countHotkeysUsed = false
  public hotkeys: HotkeyInfo[] = []
  public hotkeysCount = 0
  public mousetrap: MousetrapInstance
  private globalHotkeys: Hotkey[] = []

  constructor(
    private readonly Modal: ModalFactory,
    private readonly trainingModeManager: TrainingModeManager,
    private readonly vdOverlayService: VdOverlayService,
    private readonly vdToastNotificationService: VdToastNotificationService,
    private readonly vdTracking: VdTracking,
    private readonly webRegisterStatusManager: WebRegisterStatusManager
  ) {
    'ngInject'
    this.mousetrap = new Mousetrap()

    this.mousetrap.stopCallback = function () {
      // Allow shortcuts in inputs and textareas
      return false
    }

    this.globalHotkeys = [
      {
        action: () => this.showHotkeyTips(),
        combo: COMBO_TO_SHOW_TIPS,
        event: 'keydown',
      },
      {
        action: () => this.hideHotkeyTips(),
        combo: [
          `${COMBO_TO_SHOW_TIPS}+ctrl`,
          `${COMBO_TO_SHOW_TIPS}+mod`,
          `${COMBO_TO_SHOW_TIPS}+shift`,
        ],
        event: 'keydown',
      },
      {
        action: () => this.hideHotkeyTips(),
        combo: COMBO_TO_SHOW_TIPS,
        event: 'keyup',
      },
      {
        action: () => this.navigateToSalesHistory(),
        combo: 'f6',
        summary: 'Navigate to Sales History',
      },
      {
        action: () => this.navigateToOpenCloseRegister(),
        combo: 'f7',
        summary: 'Navigate to Open / Close Register',
      },
      {
        action: () => this.openCashDrawer(),
        combo: ['ctrl+e', 'command+e'],
        summary: 'Open the Cash Drawer',
      },
      {
        action: () => this.showHotKeysModal(),
        combo: 'alt+/',
        summary: 'Show shortcut modal',
      },
    ]
  }

  public init() {
    this.globalHotkeys.forEach((hotkey: Hotkey) => this.add(hotkey))
    this.bindEvents()
  }

  public add(hotkey: Hotkey) {
    const { combo, event, summary } = hotkey
    const inUseHotkey = this.hotkeys.find(h =>
      Boolean(h.combo === combo && h.event === event)
    )

    if (inUseHotkey) {
      $log.warn(
        `[HotkeysService] Hotkey already in use "${inUseHotkey.combo}; replacing it."`
      )
      this.remove(inUseHotkey.combo)
    }

    this.hotkeys.push({ combo, event, summary })
    this.mousetrap.bind(
      combo,
      debounce(() => this.triggerHotkey(hotkey), 300, { leading: true }),
      hotkey.event
    )
  }

  public remove(combo: Combo) {
    this.hotkeys = this.hotkeys.filter(hotkey => hotkey.combo !== combo)
    this.mousetrap.unbind(combo)
  }

  public triggerHotkey(hotkey: Hotkey) {
    const isHideTipsHotkey = Boolean(
      hotkey.combo === COMBO_TO_SHOW_TIPS && hotkey.event === 'keyup'
    )

    // Allow tips to be hidden even when a modal is open
    if (!this.isModalOpen() || isHideTipsHotkey) {
      this.trackHotKeys(hotkey)
      hotkey.action()
    }

    // Increment the hotkeys count if needed
    if (this.countHotkeysUsed && hotkey.count) {
      this.hotkeysCount++
    }

    return false
  }

  public isModalOpen() {
    return this.vdOverlayService.isVisible()
  }

  public trackHotKeys({ combo, summary }: Hotkey) {
    if (!summary) {
      return
    }

    this.vdTracking.trackUserEvent({
      eventName: 'HotKey Triggered',
      metadata: {
        combo,
        description: summary,
      },
      sendSynchronously: true,
      useSendBeacon: true,
    })
  }

  public startCountingHotkeys() {
    this.countHotkeysUsed = true
    this.hotkeysCount = 0
  }

  public stopCountingHotkeys() {
    this.countHotkeysUsed = false
    return this.hotkeysCount
  }

  private bindEvents() {
    // Hide tips when the context menu is triggered
    ;($document[0] as Document).addEventListener('contextmenu', () => {
      this.hideHotkeyTips()
    })

    // Equivalent to a `beforeprint` event listener, required because the event isn't supported in Safari
    window.matchMedia('print').addListener(mql => {
      if (mql.matches) {
        this.hideHotkeyTips()
      }
    })
  }

  private exitTrainingModeIfEnabled() {
    if (this.trainingModeManager.trainingModeEnabled) {
      return this.trainingModeManager.exitTrainingMode()
    } else {
      return $q.resolve()
    }
  }

  private navigateToSalesHistory() {
    if (this.isModalOpen()) {
      return
    }

    if (this.webRegisterStatusManager.isOnline()) {
      this.exitTrainingModeIfEnabled().then(() => {
        window.location.href = ROUTES.NEW_SALES_HISTORY_ALL
      })
    } else {
      this.vdToastNotificationService.negative({
        message: 'Sales History can only be accessed when you are online.',
      })
    }
  }

  private navigateToOpenCloseRegister() {
    if (this.isModalOpen()) {
      return
    }

    if (this.webRegisterStatusManager.isOnline()) {
      this.exitTrainingModeIfEnabled().then(() =>
        $state.go(STATES.RESOLVE_REGISTER_CLOSURE_STATE)
      )
    } else {
      this.vdToastNotificationService.negative({
        message: 'You can only open or close registers when you are online.',
      })
    }
  }

  private openCashDrawer() {
    $rootScope.$emit(EVENT.PRINT, { type: 'open-drawer' })
  }

  private showHotkeyTips() {
    ;($document[0] as Document).body.classList.add(SHOW_HOTKEY_TIPS_CLASS)
  }

  private hideHotkeyTips() {
    ;($document[0] as Document).body.classList.remove(SHOW_HOTKEY_TIPS_CLASS)
  }

  private showHotKeysModal() {
    this.Modal({
      controller: HotkeysModalController,
      controllerAs: '$ctrl',
      template,
    })
  }
}
